import { throttle } from 'lodash';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutationObserverForEditor } from './useMutationObserverForEditor';
import { useResizeObserver } from './useResizeObserver';
import type {
  AnimationType,
  EffectType,
  StartPosition,
} from '../TextParticles.types';

export type ComputedTextStyle = {
  color: string;
  font: string;
  letterSpacing: string;
  textTransform: string;
  boundingBox: DOMRect;
  effectType: EffectType;
  animationType: AnimationType;
  animationDuration: number;
  particleStartSize: number;
  particleEndSize: number;
  particleStartOpacity: number;
  particleEndOpacity: number;
  windDirection: number;
  animationDirection: number;
  speed: number;
  turbulence: number;
  particleColorCount: number;
  particleColor1: string;
  particleColor2: string;
  particleColor3: string;
  particleColor4: string;
  startPosition: StartPosition;
  noise: number;
  amplitude: number;
  presetId: string;
  particleDuration: number;
};

export const useComputedStyle = (
  compId: string,
  elementRef: React.RefObject<HTMLElement | null>,
  isEditor: boolean,
) => {
  const [computedStyle, setComputedStyle] = useState<
    ComputedTextStyle | undefined
  >(undefined);

  const previousComputedStyleCacheRef = useRef<string>('');

  const getComputedStyleCache = useCallback(
    (_computedStyle: ComputedTextStyle | undefined) => {
      if (!_computedStyle) {
        return '';
      }

      const boundingBoxData = {
        width: Math.round(_computedStyle.boundingBox.width),
        height: Math.round(_computedStyle.boundingBox.height),
      };

      const cacheData = {
        ..._computedStyle,
        boundingBox: boundingBoxData,
      };

      return JSON.stringify(cacheData);
    },
    [],
  );

  const applyComputedStyle = useCallback(() => {
    // assumes styles changed & fonts loaded
    const boundingBox = elementRef.current?.getBoundingClientRect();

    if (
      !elementRef.current ||
      !boundingBox ||
      (boundingBox.width === 0 && boundingBox.height === 0)
    ) {
      setComputedStyle(undefined);
    } else {
      const nextComputedStyle = window.getComputedStyle(elementRef.current);
      const nextTextTransform =
        nextComputedStyle.getPropertyValue('text-transform') ||
        nextComputedStyle.getPropertyValue('--text-transform');
      const nextTextColor =
        nextComputedStyle.getPropertyValue('color') ||
        nextComputedStyle.getPropertyValue('--color');
      const nextEffectType =
        nextComputedStyle.getPropertyValue('--effect-type');
      const nextAnimationType =
        nextComputedStyle.getPropertyValue('--animation-type');
      const nextAnimationDuration = Number(
        nextComputedStyle.getPropertyValue('--duration'),
      );
      const nextParticleStartSize = Number(
        nextComputedStyle.getPropertyValue('--particle-start-size'),
      );
      const nextParticleEndSize = Number(
        nextComputedStyle.getPropertyValue('--particle-end-size'),
      );
      const nextParticleStartOpacity = Number(
        nextComputedStyle.getPropertyValue('--particle-start-opacity'),
      );
      const nextParticleEndOpacity = Number(
        nextComputedStyle.getPropertyValue('--particle-end-opacity'),
      );
      const nextWindDirection = Number(
        nextComputedStyle.getPropertyValue('--wind-direction'),
      );
      const nextAnimationDirection = Number(
        nextComputedStyle.getPropertyValue('--animation-direction'),
      );
      const nextSpeed = Number(nextComputedStyle.getPropertyValue('--speed'));
      const nextTurbulence = Number(
        nextComputedStyle.getPropertyValue('--turbulence'),
      );
      const nextParticleColorCount = Number(
        nextComputedStyle.getPropertyValue('--particle-color-count'),
      );
      const nextParticleColor1 = nextComputedStyle.getPropertyValue(
        '--text-particles-color-1',
      );
      const nextParticleColor2 = nextComputedStyle.getPropertyValue(
        '--text-particles-color-2',
      );
      const nextParticleColor3 = nextComputedStyle.getPropertyValue(
        '--text-particles-color-3',
      );
      const nextParticleColor4 = nextComputedStyle.getPropertyValue(
        '--text-particles-color-4',
      );
      const nextStartPosition = nextComputedStyle.getPropertyValue(
        '--start-position',
      ) as StartPosition;
      const nextNoise = Number(nextComputedStyle.getPropertyValue('--noise'));
      const nextAmplitude = Number(
        nextComputedStyle.getPropertyValue('--amplitude'),
      );
      const nextPresetId = nextComputedStyle.getPropertyValue('--preset-id');
      const nextParticleDuration = Number(
        nextComputedStyle.getPropertyValue('--particle-duration'),
      );
      const nextTextComputedStyle: ComputedTextStyle = {
        font: nextComputedStyle.font,
        letterSpacing: nextComputedStyle.letterSpacing,
        textTransform: nextTextTransform,
        boundingBox,
        color: nextTextColor,
        effectType: nextEffectType as EffectType,
        animationType: nextAnimationType as AnimationType,
        animationDuration: nextAnimationDuration,
        particleStartSize: nextParticleStartSize,
        particleEndSize: nextParticleEndSize,
        particleStartOpacity: nextParticleStartOpacity,
        particleEndOpacity: nextParticleEndOpacity,
        windDirection: nextWindDirection,
        animationDirection: nextAnimationDirection,
        speed: nextSpeed,
        turbulence: nextTurbulence,
        particleColorCount: nextParticleColorCount,
        particleColor1: nextParticleColor1,
        particleColor2: nextParticleColor2,
        particleColor3: nextParticleColor3,
        particleColor4: nextParticleColor4,
        startPosition: nextStartPosition,
        noise: nextNoise,
        amplitude: nextAmplitude,
        presetId: nextPresetId,
        particleDuration: nextParticleDuration,
      };

      const previousComputedStyleCache = previousComputedStyleCacheRef.current;
      const nextComputedStyleCache = getComputedStyleCache(
        nextTextComputedStyle,
      );

      if (previousComputedStyleCache !== nextComputedStyleCache) {
        previousComputedStyleCacheRef.current = nextComputedStyleCache;
        setComputedStyle(nextTextComputedStyle);
      }
    }
  }, [elementRef, getComputedStyleCache]);

  const applyComputedStyleAfterFontsReady = useCallback(() => {
    document.fonts.ready.then(applyComputedStyle, () => {});
  }, [applyComputedStyle]);

  const applyComputedStyleAfterFontsReadyThrottled = useMemo(() => {
    return throttle(applyComputedStyleAfterFontsReady, 100, {
      leading: true,
      trailing: true,
    });
  }, [applyComputedStyleAfterFontsReady]);

  useMutationObserverForEditor({
    isEditor,
    compId,
    onStyleChange: applyComputedStyleAfterFontsReady,
  });

  useResizeObserver({
    elementRef,
    onResize: applyComputedStyleAfterFontsReadyThrottled,
  });

  useEffect(() => {
    applyComputedStyleAfterFontsReady();
  }, [applyComputedStyleAfterFontsReady]);

  return computedStyle;
};
export type UseComputedStyleResult = ReturnType<typeof useComputedStyle>;
